import React, { useState, useEffect } from 'react';
import { createFaq, updateFaq } from '../../../services/api';
import { Faq } from '../../../types';
import { useTranslation } from '../../../context/TranslationContext';
import { useAuth } from '../../../context/AuthContext';
import './FaqModal.css';

interface FaqModalProps {
  faq: Faq | null;
  onClose: (refreshNeeded: boolean) => void;
}

const FaqModal: React.FC<FaqModalProps> = ({ faq, onClose }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  
  const [question, setQuestion] = useState<string>('');
  const [answer, setAnswer] = useState<string>('');
  const [order, setOrder] = useState<number>(0);
  const [active, setActive] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setOrder(faq.order);
      setActive(faq.active);
    }
  }, [faq]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError(t('admin.common.error'));
      return;
    }
    
    if (!question.trim() || !answer.trim()) {
      setError(t('admin.faqs.questionAnswerRequired'));
      return;
    }
    
    try {
      setLoading(true);
      
      const faqData = {
        question,
        answer,
        order,
        active
      };
      
      if (faq) {
        // Update existing FAQ
        const id = faq.id?.toString() || faq._id?.toString() || '';
        await updateFaq(id, faqData, token);
      } else {
        // Create new FAQ
        await createFaq(faqData, token);
      }
      
      onClose(true);
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{faq ? t('admin.faqs.editFaq') : t('admin.faqs.addFaq')}</h2>
          <button 
            className="close-button"
            onClick={() => onClose(false)}
          >
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}
            
            <div className="form-group">
              <label htmlFor="question">{t('admin.faqs.question')}</label>
              <input
                type="text"
                id="question"
                className="form-control"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="answer">{t('admin.faqs.answer')}</label>
              <textarea
                id="answer"
                className="form-control"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={5}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="order">{t('admin.faqs.order')}</label>
              <input
                type="number"
                id="order"
                className="form-control"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                min={0}
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                {t('admin.faqs.active')}
              </label>
            </div>
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              {t('admin.faqs.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? t('admin.common.loading') : t('admin.faqs.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FaqModal;
